// Runtime de TypeScript/JavaScript.
//
// Flow:
//   1. Code del candidato → Monaco TS worker → JS (transpilado, sin chequear tipos)
//   2. Detectar todas las `class X { ... }` declaradas top-level en el código
//   3. Evaluar el array de tests del ejercicio dentro del mismo scope que el código
//      del candidato (closure: las clases declaradas son visibles dentro de fn).
//   4. Para cada test, invocar `test.fn(PrimaryClass, allClasses)` donde:
//      - PrimaryClass = la última clase declarada (típicamente la solución)
//      - allClasses = objeto { ClassName: ClassRef } con todas las top-level
//   Esto soporta ambos formatos de tests:
//      • Legacy:  `fn: (RL) => new RL(3, 1000)`
//      • Closure: `fn: () => new MyClass(3, 1000)`
//      • Explícito: `fn: (_, all) => new all.MyClass(...)`

import type { Monaco } from "@monaco-editor/react";
import { buildHelpers } from "./test-helpers";
import type { RunResult, RuntimeInput, TestRunResult } from "./types";

async function transpileTSWithMonaco(
  monaco: Monaco,
  code: string,
  modelUri?: string
): Promise<string> {
  let uriObj;
  if (modelUri) {
    uriObj = monaco.Uri.parse(modelUri);
  } else {
    const tempModel = monaco.editor.createModel(
      code,
      "typescript",
      monaco.Uri.parse(`file:///temp-${Date.now()}.ts`)
    );
    uriObj = tempModel.uri;
    setTimeout(() => tempModel.dispose(), 100);
  }

  const workerProvider = await monaco.languages.typescript.getTypeScriptWorker();
  const worker = await workerProvider(uriObj);
  const output = await worker.getEmitOutput(uriObj.toString());
  const jsFile = output.outputFiles.find((f: { name: string }) =>
    f.name.endsWith(".js")
  );
  if (!jsFile) {
    throw new Error("No se pudo transpilar TypeScript.");
  }
  return jsFile.text;
}

interface RunTsParams extends RuntimeInput {
  monaco: Monaco;
  modelUri?: string;
}

/**
 * Extrae todos los nombres de clases declaradas top-level con regex.
 * Cubre: `class X`, `class X extends Y`, `class X<T>`.
 * NO cubre: `const X = class {}` (assignment a class expression) — esos no son
 * top-level identifiers en sentido estricto.
 *
 * Retorna en orden de aparición. La última se considera la solución principal.
 */
function findClassNames(jsCode: string): string[] {
  const re = /\bclass\s+([A-Za-z_$][\w$]*)/g;
  const names: string[] = [];
  let m: RegExpExecArray | null;
  while ((m = re.exec(jsCode)) !== null) {
    names.push(m[1]);
  }
  return names;
}

export async function runTsExercise(params: RunTsParams): Promise<RunResult> {
  const { monaco, code, exerciseTestsCode, modelUri } = params;

  if (!exerciseTestsCode?.trim()) {
    return {
      results: [],
      passedCount: 0,
      totalCount: 0,
      score: 0,
      fatalError: "Este ejercicio no tiene tests configurados.",
    };
  }

  let jsCode: string;
  try {
    jsCode = await transpileTSWithMonaco(monaco, code, modelUri);
  } catch (e) {
    return {
      results: [],
      passedCount: 0,
      totalCount: 0,
      score: 0,
      fatalError: `Error transpilando TypeScript: ${
        e instanceof Error ? e.message : String(e)
      }`,
    };
  }

  const allClassNames = findClassNames(jsCode);
  if (allClassNames.length === 0) {
    return {
      results: [],
      passedCount: 0,
      totalCount: 0,
      score: 0,
      fatalError:
        "No se encontró ninguna clase declarada en tu código. Asegurate de declarar tu solución con `class TuNombre extends ...`.",
    };
  }
  const primaryClassName = allClassNames[allClassNames.length - 1];

  // Construimos un bundle que retorna:
  //   - primary: la clase principal (última declarada)
  //   - all: objeto con todas las clases por nombre
  //   - tests: el array de tests del ejercicio (con closure sobre las clases)
  const allEntries = allClassNames
    .map((n) => `${JSON.stringify(n)}: typeof ${n} !== 'undefined' ? ${n} : undefined`)
    .join(", ");

  let testsRaw: { name: string; fn: (...args: unknown[]) => unknown }[];
  let primaryClass: unknown;
  let allClasses: Record<string, unknown>;
  try {
    const helpers = buildHelpers();
    // eslint-disable-next-line @typescript-eslint/no-implied-eval
    const fnBuilder = new Function(
      "__helpers",
      `
      // Inyectamos los helpers como locales en el scope del eval
      const test = __helpers.test;
      const expect = __helpers.expect;
      const measureUnder = __helpers.measureUnder;

      ${jsCode}
      ;return {
        primary: typeof ${primaryClassName} !== 'undefined' ? ${primaryClassName} : null,
        all: { ${allEntries} },
        tests: (${exerciseTestsCode})
      };
    `
    );
    const result = fnBuilder(helpers);
    if (!Array.isArray(result.tests)) {
      throw new Error("El array de tests del ejercicio no es válido.");
    }
    testsRaw = result.tests;
    primaryClass = result.primary;
    allClasses = result.all;
  } catch (e) {
    return {
      results: [],
      passedCount: 0,
      totalCount: 0,
      score: 0,
      fatalError: `Error preparando tests: ${
        e instanceof Error ? e.message : String(e)
      }`,
    };
  }

  if (!primaryClass) {
    return {
      results: [],
      passedCount: 0,
      totalCount: 0,
      score: 0,
      fatalError: `No se pudo cargar tu clase \`${primaryClassName}\` en el runtime.`,
    };
  }

  const results: TestRunResult[] = [];
  let passed = 0;

  for (const t of testsRaw) {
    let ok = false;
    let err: string | undefined;
    try {
      // Pasamos la clase principal como primer arg, todas las clases como segundo.
      // Tests legacy:    fn: (RL) => boolean   → RL = primary, retorna true/false
      // Tests con expect:fn: (RL) => void      → si no tira, pasa
      // Tests closure:   fn: () => ...         → usa nombre por closure
      const out = await Promise.resolve(t.fn(primaryClass, allClasses));
      // false explícito = fail. undefined/true/otro truthy = pass.
      ok = out !== false;
    } catch (e) {
      err = e instanceof Error ? e.message : String(e);
    }
    if (ok) passed++;
    results.push({ name: t.name ?? "(sin nombre)", passed: ok, error: err });
  }

  return {
    results,
    passedCount: passed,
    totalCount: testsRaw.length,
    score:
      testsRaw.length === 0 ? 0 : Math.round((passed / testsRaw.length) * 100),
  };
}
